import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Clock, ExternalLink, Heart, Pencil, Users } from 'lucide-react';
import { api, photoUrl, photoSrcSet } from '@/lib/api';
import { useIsAuthenticated } from '@/lib/auth';
import { useFavourites } from '@/lib/favourites';
import { useCategoryMap } from '@/lib/useCategoryMap';
import IngredientList from '@/components/IngredientList';
import StepList from '@/components/StepList';
import { formatMinutes, totalMinutes } from '@/lib/format';
import { cn } from '@/lib/cn';

export default function RecipeDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const { isFavourite, toggle } = useFavourites();

  const recipeQuery = useQuery({
    queryKey: ['recipe', slug],
    queryFn: () => api.getRecipeBySlug(slug!),
    enabled: !!slug,
  });
  const { categories: allCategories } = useCategoryMap();

  if (recipeQuery.isPending) {
    return (
      <div className="mx-auto max-w-2xl px-5 pt-6">
        <div className="h-72 animate-pulse rounded-2xl bg-accent-soft" />
      </div>
    );
  }
  if (recipeQuery.isError || !recipeQuery.data) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 px-5 pt-6">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1 text-sm text-text-muted"
        >
          <ChevronLeft size={16} /> Zpět
        </button>
        <p className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Recept nebyl nalezen.
        </p>
      </div>
    );
  }

  const recipe = recipeQuery.data;
  const url = photoUrl(recipe.photo_key);
  const total = formatMinutes(totalMinutes(recipe.prep_minutes, recipe.cook_minutes));
  const categories = allCategories.filter((c) => recipe.category_ids.includes(c.id));
  const isFav = isAuthenticated && isFavourite(recipe.id);

  const ownerActions = isAuthenticated && (
    <>
      <Link
        to={`/admin/recipe/${recipe.id}`}
        aria-label="Upravit"
        className="rounded-full bg-white/80 p-2 text-text backdrop-blur-md"
      >
        <Pencil size={18} />
      </Link>
      <button
        type="button"
        onClick={() => toggle(recipe.id)}
        aria-label="Oblíbené"
        className="rounded-full bg-white/80 p-2 backdrop-blur-md"
      >
        <Heart size={20} className={cn(isFav ? 'fill-heart text-heart' : 'text-text')} />
      </button>
    </>
  );

  return (
    <article className="mx-auto max-w-2xl pb-16">
      {url ? (
        <div className="relative">
          <img
            src={url}
            srcSet={photoSrcSet(recipe.photo_key)}
            sizes="(min-width: 672px) 672px, 100vw"
            alt={recipe.title}
            decoding="async"
            fetchPriority="high"
            className="h-72 w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/25" />
          <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Zpět"
              className="rounded-full bg-white/80 p-2 text-text backdrop-blur-md"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex items-center gap-2">{ownerActions}</div>
          </div>
          <div className="absolute inset-x-0 bottom-0 p-5 text-white">
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {categories.map((c) => (
                  <Link
                    key={c.id}
                    to={`/?category=${c.slug}`}
                    className="inline-block rounded-full bg-white/80 px-2.5 py-1 text-[11px] font-bold text-text backdrop-blur-md"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
            <h1 className="mt-2 font-serif text-2xl font-extrabold leading-tight">
              {recipe.title}
            </h1>
          </div>
        </div>
      ) : (
        <div className="px-5 pt-6">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Zpět"
              className="rounded-full bg-accent-soft p-2 text-text hover:brightness-95"
            >
              <ChevronLeft size={18} />
            </button>
            {isAuthenticated && (
              <div className="flex items-center gap-2">
                <Link
                  to={`/admin/recipe/${recipe.id}`}
                  className="rounded-full bg-accent-soft p-2 text-text"
                  aria-label="Upravit"
                >
                  <Pencil size={16} />
                </Link>
                <button
                  type="button"
                  onClick={() => toggle(recipe.id)}
                  className="rounded-full bg-accent-soft p-2"
                  aria-label="Oblíbené"
                >
                  <Heart size={18} className={cn(isFav ? 'fill-heart text-heart' : 'text-text')} />
                </button>
              </div>
            )}
          </div>
          {categories.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/?category=${c.slug}`}
                  className="inline-block rounded-full bg-accent-soft px-2.5 py-1 text-[11px] font-bold text-text-muted hover:text-accent"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          )}
          <h1 className="mt-1 font-serif text-3xl font-extrabold leading-tight">{recipe.title}</h1>
        </div>
      )}

      <div className="px-5 pt-5">
        <div className="flex flex-wrap gap-2">
          {total && (
            <span className="inline-flex items-center gap-1.5 rounded-xl bg-accent-soft px-3 py-2 text-sm font-medium text-text-muted">
              <Clock size={15} /> {total}
            </span>
          )}
          {recipe.servings && (
            <span className="inline-flex items-center gap-1.5 rounded-xl bg-accent-soft px-3 py-2 text-sm font-medium text-text-muted">
              <Users size={15} /> {recipe.servings} porcí
            </span>
          )}
        </div>

        {recipe.description && (
          <p className="mt-4 text-[15px] leading-relaxed text-text-muted">{recipe.description}</p>
        )}

        <div className="mt-6">
          <IngredientList recipeId={recipe.id} ingredients={recipe.ingredients} />
        </div>
        <div className="mt-7">
          <StepList steps={recipe.steps} />
        </div>

        {recipe.links.length > 0 && (
          <section className="mt-7 space-y-2">
            <h2 className="mb-1 font-serif text-lg font-bold">Odkazy</h2>
            {recipe.links.map((link, idx) => (
              <a
                key={idx}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 rounded-xl border border-border px-4 py-3 transition-colors hover:bg-accent-soft"
              >
                <ExternalLink size={16} className="shrink-0 text-text" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-medium text-text">
                    {link.label || linkLabel(link.url)}
                  </span>
                  <span className="block truncate text-xs text-text-muted">
                    {linkLabel(link.url)}
                  </span>
                </span>
              </a>
            ))}
          </section>
        )}

        {recipe.notes && (
          <section className="mt-7">
            <h2 className="mb-2 font-serif text-lg font-bold">Poznámky</h2>
            <p className="whitespace-pre-wrap rounded-2xl bg-accent-soft p-4 text-[15px] leading-relaxed text-text-muted">
              {recipe.notes}
            </p>
          </section>
        )}
      </div>
    </article>
  );
}

function linkLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
